import React, { forwardRef, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { containPopupFocus } from './popupKeyboard';

// Keep dialogs outside chart/sidebar stacking and clipping contexts.
const PlatformModalLayer = forwardRef(function PlatformModalLayer({ children, className = '', onDismiss, ...props }, ref) {
  const layer = useRef(null);
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;
  const hasDismiss = Boolean(onDismiss);
  const modalRole = props.role;
  const modalLabel = props['aria-label'];
  useEffect(() => {
    if (!hasDismiss || modalRole === 'false' || modalRole === false) return;
    const card = layer.current.querySelector('.card, .window-module') || layer.current;
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'true');
    if (modalLabel) card.setAttribute('aria-label', modalLabel);
    const keyboard = containPopupFocus(card, { onDismiss: () => dismiss.current?.() });
    return () => keyboard.dispose();
  }, [hasDismiss, modalRole, modalLabel]);
  const drag = useRef(null);
  const movedCard = useRef(null);
  const clamp = (card, x, y) => {
    const previous = card.__popupOffset || { x: 0, y: 0 };
    const rect = card.getBoundingClientRect();
    const left = rect.left - previous.x, top = rect.top - previous.y;
    const next = {
      x: Math.max(8 - left, Math.min(x, window.innerWidth - rect.width - 8 - left)),
      y: Math.max(8 - top, Math.min(y, window.innerHeight - rect.height - 8 - top)),
    };
    card.__popupOffset = next;
    card.style.translate = `${next.x}px ${next.y}px`;
  };
  useEffect(() => {
    const resize = () => {
      const card = movedCard.current;
      if (card?.isConnected) {
        const { x, y } = card.__popupOffset;
        clamp(card, x, y);
      }
    };
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);
  const startDrag = (event) => {
    if (event.button !== 0 || event.target.closest('button, a, input, [title], [data-tooltip], [role="button"]')) return;
    const header = event.target.closest('.wm-header, .deposit-dialog-header, .withdraw-dialog-header, .price-alert-header');
    const card = header?.closest('.window-module, .card');
    if (!card) return;
    event.preventDefault();
    const offset = card.__popupOffset || { x: 0, y: 0 };
    drag.current = { card, x: event.clientX, y: event.clientY, offset };
    card.classList.add('is-dragging');
    movedCard.current = card;
    header.setPointerCapture(event.pointerId);
  };
  const moveDrag = (event) => {
    const active = drag.current;
    if (active) clamp(active.card, active.offset.x + event.clientX - active.x, active.offset.y + event.clientY - active.y);
  };
  const stopDrag = () => {
    drag.current?.card.classList.remove('is-dragging');
    drag.current = null;
  };
  return createPortal(
    <div {...props} ref={node => { layer.current = node; if (typeof ref === 'function') ref(node); else if (ref) ref.current = node; }}
      role={props.role}
      onClick={event => { if (event.target === event.currentTarget) dismiss.current?.(); }} className={`${className} platform-modal-layer`}
      onPointerDown={startDrag} onPointerMove={moveDrag}
      onPointerUp={stopDrag} onPointerCancel={stopDrag} onLostPointerCapture={stopDrag}>
      {children}
    </div>,
    document.body,
  );
});

export default PlatformModalLayer;
